import React, { FC } from 'react';

import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';

import { styles } from './styles';
import MessageImageItem from '@components/common/MessageImageItem';

import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';
import ArrowRightLinear from '@components/icons/ArrowRightLinear';
import ArrowLeftLinear from '@components/icons/ArrowLeftLinear';
import { IStoryPromptImage } from '@bus/storyTeller/typedefs';
import { ratioSplitting } from '@helpers/ratioSplitting';
import { useDispatch, useSelector } from 'react-redux';
import { storyTellerActions } from '@bus/storyTeller/actions';
import {
  getImageRegenerateImageLimit,
  getProfile,
} from '@bus/profile/selectors';
import { useSnackbar } from 'notistack';
import usePermissionCheck from '../../../../../hooks/usePermissionCheck';

type StoryImagesListProps = {
  messageId: number;
  progressId?: number;
  images: IStoryPromptImage[];
  promptId: number;
  chatId: number;
  ratio: string;
  regenerateLimit: number;
};

export const StoryImagesList: FC<StoryImagesListProps> = ({
  messageId,
  progressId,
  images,
  promptId,
  chatId,
  ratio,
  regenerateLimit,
}) => {
  const theme = useTheme();
  const imageLimit = useSelector(getImageRegenerateImageLimit);
  const profile = useSelector(getProfile);
  const dispatch = useDispatch();
  const [widthRatio, heightRatio] = ratioSplitting('x', ratio);
  const { enqueueSnackbar } = useSnackbar();
  const { notification } = usePermissionCheck();

  const handleRegenerateImage = () => {
    if (profile?.subscription && regenerateLimit >= imageLimit) {
      enqueueSnackbar(
        'You have reach the limit of free regenerations per story. Please create a new one',
        {
          autoHideDuration: 5000,
          variant: 'infoSnackbar',
        },
      );

      return;
    }

    if (notification(`Can't regenerate image`)) {
      return;
    }

    dispatch(
      storyTellerActions.regenerateImage({
        chatId,
        promptId,
      }),
    );
  };

  return (
    <Box sx={() => styles.storyImagesList(widthRatio, heightRatio)}>
      {messageId === progressId || !!images.length ? (
        <>
          {images.length <= 1 ? (
            <Box sx={() => styles.wrapper(widthRatio, heightRatio)}>
              <MessageImageItem
                id={1}
                image={images[0]?.image ?? ''}
                isError={''}
                ratio={ratio}
                chatId={1}
                messageId={1}
                shortImage={'string'}
                isSwap={false}
                type={'storyteller'}
                isDeleted={false}
              />
              <Box display={'flex'} justifyContent={'center'} mt={'32px'}>
                <Button
                  variant={'primary'}
                  size={'40'}
                  sx={{ maxWidth: '208px' }}
                  fullWidth
                  onClick={handleRegenerateImage}
                  disabled={!!progressId}>
                  <Typography variant={'body1'}>Regenerate</Typography>
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Swiper
                modules={[Navigation, Pagination]}
                pagination={{
                  type: 'custom',
                  renderCustom: (swiper: any, current: any, total: any) => {
                    const currentElement = document.querySelector(
                      `.suggestion-slider-current-${promptId}`,
                    );
                    if (currentElement) currentElement.textContent = current;

                    const totalElement = document.querySelector(
                      `.suggestion-slider-total-${promptId}`,
                    );
                    if (totalElement) totalElement.textContent = total;

                    return '';
                  },
                }}
                spaceBetween={10}
                slidesPerView={'auto'}
                navigation={{
                  nextEl: `.suggestion-slider-next-${promptId}`,
                  prevEl: `.suggestion-slider-prev-${promptId}`,
                }}
                style={{ zIndex: 0 }}>
                {images.map((image) => (
                  <SwiperSlide key={image.id}>
                    <Box>
                      <MessageImageItem
                        id={1}
                        image={image.image ?? ''}
                        isError={''}
                        ratio={ratio}
                        chatId={1}
                        messageId={1}
                        shortImage={'string'}
                        isSwap={false}
                        type={'storyteller'}
                        isDeleted={false}
                      />
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
              <Box sx={styles.nav}>
                <Box maxWidth={'60px'} width={'100%'} display={'flex'}>
                  <Typography
                    className={`suggestion-slider-current-${promptId}`}
                    variant={'body1'}
                    color={'common.brand'}
                  />
                  <Typography
                    mx={'5px'}
                    variant={'body1'}
                    color={'common.brand'}>
                    /
                  </Typography>
                  <Typography
                    className={`suggestion-slider-total-${promptId}`}
                    variant={'body1'}
                    color={'common.brand'}
                  />
                </Box>
                <Button
                  variant={'primary'}
                  fullWidth
                  size={'40'}
                  onClick={handleRegenerateImage}
                  disabled={!!progressId}>
                  <Typography variant={'body1'}>Regenerate</Typography>
                </Button>
                <Box display={'flex'} alignItems={'center'} gap={'12px'}>
                  <IconButton
                    className={`suggestion-slider-control suggestion-slider-prev-${promptId}`}>
                    <ArrowLeftLinear
                      fontSize={'small'}
                      htmlColor={theme.palette.common.surface['surface 4']}
                    />
                  </IconButton>
                  <IconButton
                    className={`suggestion-slider-control suggestion-slider-next-${promptId}`}>
                    <ArrowRightLinear
                      fontSize={'small'}
                      htmlColor={theme.palette.common.surface['surface 4']}
                    />
                  </IconButton>
                </Box>
              </Box>
            </>
          )}
        </>
      ) : (
        <Typography
          width={'100%'}
          display={'inline-block'}
          variant={'body2'}
          color={'error.main'}
          textAlign={'center'}>
          Image was not generated
        </Typography>
      )}
    </Box>
  );
};

export default StoryImagesList;
